import type { SyntheticEvent } from 'react'

/**
 * The new file editor (FileViewer / KonvaInner) loads images with
 * crossOrigin="anonymous" so the Konva canvas export isn't tainted. On
 * Safari/WebKit a CORS and a non-CORS `<img>` to the same URL share one HTTP
 * cache entry that does NOT vary on request mode, so a non-CORS thumbnail can
 * poison the entry and make the viewer's later CORS load fail (blank image).
 * Render every `<img>` of a given file with the SAME crossOrigin value so the
 * shared cache entry stays CORS-clean.
 *
 * Provider-hosted files (e.g. CompanyCam) point at external hosts that may not
 * return Access-Control-Allow-Origin at all; forcing crossOrigin on them would
 * guarantee a failed load, so opt them out. Those files are display-only —
 * in-app editing is already disabled for them.
 *
 * Keyless remote files (see isKeylessRemoteFile) are opted out for the same
 * reason: their `src` is still the caller's URL on an unknown host (#24892).
 */
export function imageCrossOrigin(file?: unknown): 'anonymous' | undefined {
  // `file` is typed loosely so this works against both the global `paintscout`
  // QuoteFile (which declares `provider`) and the leaner local QuoteFile types.
  const provider = (file as { provider?: unknown } | null | undefined)?.provider
  return provider || isKeylessRemoteFile(file) ? undefined : 'anonymous'
}

/**
 * True for a file whose `src` is an external URL that was never copied into S3 or
 * Cloudinary: a remote import that is still `pending` or that `failed` (#24884,
 * #24885), or an older public-API quote-item file. These files have no key to build
 * a Cloudinary video/PDF render from, and their host may not send CORS headers.
 * Provider files (e.g. CompanyCam) are handled separately and are not keyless.
 */
export function isKeylessRemoteFile(file?: unknown): boolean {
  const f = file as
    | { src?: unknown; s3PublicKey?: unknown; cloudinaryPublicId?: unknown; provider?: unknown }
    | null
    | undefined
  return !!f?.src && !f.s3PublicKey && !f.cloudinaryPublicId && !f.provider
}

/**
 * onError handler for a CORS `<img>`. Mobile Safari can serve a cache entry
 * that was first populated by a non-CORS request (and therefore lacks CORS
 * headers), which fails the CORS check. Retry once with a cache-buster query
 * param to force a fresh CORS-headers fetch instead of the poisoned entry.
 * Mirrors the imperative retry in FileViewer/KonvaInner's useImage().
 */
export function retryCorsImageOnError(e: SyntheticEvent<HTMLImageElement>): void {
  const img = e.currentTarget
  if (img.dataset.psCorsRetried) {
    return
  }
  img.dataset.psCorsRetried = '1'
  const src = img.currentSrc || img.src
  if (!src) {
    return
  }
  img.src = src + (src.includes('?') ? '&' : '?') + '__ps_cors=1'
}
